import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useDebounce, useDebouncedCallback } from 'use-debounce';
import { Typography } from '@mui/material';

import NumberTypo from 'src/components/common/Number';
import Tooltip from 'src/components/common/Tooltip';
import GradientButton from 'src/components/GradientButton';
import Image from 'src/components/Image';
import TransactionBottomSheet from 'src/components/TransactionBottomSheet';
import { DYDX_TOKEN, USDC_TOKEN } from 'src/constants/tokens';
import { useAccountSWR } from 'src/hooks/SWR/cosmos/useAccountSWR';
import { useAssetsSWR } from 'src/hooks/SWR/cosmos/useAssetsSWR';
import { useCosmosBalanceSWR } from 'src/hooks/SWR/cosmos/useCosmosBalanceSWR';
import { useSimulateSWR } from 'src/hooks/SWR/cosmos/useSimulateSWR';
import { useTxInfoSWR } from 'src/hooks/SWR/cosmos/useTxInfoSWR';
import { useCurrentAccount } from 'src/hooks/useAccount';
import { getSendDirectDoc, getSimulateSendTxBytes, signDirectTx } from 'src/utils/cosmos';
import { mnemonicToPair } from 'src/utils/crypto';
import { pageView, transferClickEvent } from 'src/utils/ga';
import { ceil, fix, gt, isDecimal, minus, times, toBaseDenomAmount, toDisplayDenomAmount, trimZeros } from 'src/utils/number';
import { getCosmosAddressRegex } from 'src/utils/regex';
import { isEqualsIgnoringCase, isMnemonic } from 'src/utils/string';

import CoinBottomSheet from './components/CoinBottomSheet';
import SignConfirmBottomSheet from './components/SignConfirmBottomSheet';
import TxDetails from './components/TxDetails';
import {
  AmountPresetButton,
  Container,
  ContentsContainer,
  DetailContainer,
  DYDXBodyContainer,
  DYDXContainer,
  DYDXIconContainer,
  DYDXTopContainer,
  DYDXTopLeftAmountContainer,
  DYDXTopLeftContainer,
  DYDXTopRightContainer,
  Header,
  HeaderTitle,
  ImageContainer,
  InputContainer,
  RightChevronIconContainer,
  StyledInput,
  StyledTextarea,
  TokenButton,
  TokenRightContainer,
  TokenTextContainer,
  TokenTextTopContainer,
  WrapperContainer,
} from './styled';
import { Base02TextContainer } from '../Staking/styled';

import { ReactComponent as DYDXIcon } from 'src/assets/svg/DYDX.svg';
import { ReactComponent as RightChevronIcon } from 'src/assets/svg/RightChevron.svg';
import { ReactComponent as USDCIcon } from 'src/assets/svg/USDC.svg';

export default function Send() {
  const { enqueueSnackbar } = useSnackbar();

  const { currentAccount } = useCurrentAccount();
  const account = useAccountSWR(currentAccount.cosmosAddress);
  const balance = useCosmosBalanceSWR(currentAccount.cosmosAddress);

  const assets = useAssetsSWR();

  const [currentDisplayAmount, setCurrentDisplayAmount] = useState('');

  // TODO 수신인 주소 입력
  const [isDisabled, setIsDisabled] = useState(false);

  const [isOpenedCoinBottomSheet, setIsOpenedCoinBottomsheet] = useState(false);
  const [isOpenSignConfirmBottomsheet, setIsOpenSignConfirmBottomsheet] = useState(false);
  const [isOpenTrasactionBottomsheet, setIsOpenTrasactionBottomsheet] = useState(false);

  const [isTxTrackingFailed, setIsTxTrackingFailed] = useState(false);
  const [isTxTrackingSuccess, setIsTxTrackingSucces] = useState(false);

  const [currentMemo, setCurrentMemo] = useState('');
  const [transactionHash, setTransactionHash] = useState('');

  const [debouncedCurrentDisplayAmount] = useDebounce(currentDisplayAmount, 500);
  const [debouncedCurrentMemo] = useDebounce(currentMemo, 500);

  const [currentReceiverAddress, setCurrentReceiverAddress] = useState('');
  const [currentCoinDenom, setCurrentCoinDenom] = useState(DYDX_TOKEN.denom);

  const availableCoins = useMemo(
    () =>
      assets.data?.map((item) => {
        const tokenBalance = balance.data?.balances?.find((balanceItem) => balanceItem.denom === item.denom)?.amount || '0';
        return {
          ...item,
          image: item.image ? `https://raw.githubusercontent.com/cosmostation/chainlist/main/chain/${item.image}` : '',
          balance: tokenBalance,
        };
      }) || [],
    [assets.data, balance.data?.balances],
  );

  const currentCoin = useMemo(() => availableCoins.find((item) => isEqualsIgnoringCase(item.denom, currentCoinDenom)), [availableCoins, currentCoinDenom]);

  const baseDebouncedCurrentDisplayAmount = useMemo(
    () => toBaseDenomAmount(debouncedCurrentDisplayAmount || '0', currentCoin?.decimals || 0),
    [currentCoin?.decimals, debouncedCurrentDisplayAmount],
  );

  const currentCoinBaseAmount = useMemo(
    () => availableCoins.find((item) => isEqualsIgnoringCase(item.denom, currentCoin?.denom))?.balance || '0',
    [availableCoins, currentCoin?.denom],
  );

  const addressRegex = useMemo(() => getCosmosAddressRegex('dydx', [39]), []);

  const simulateDirectTxBytes = useMemo(
    () =>
      account.data?.account.account_number &&
      currentAccount.mnemonic &&
      account.data?.account.sequence &&
      currentReceiverAddress &&
      currentCoin &&
      gt(baseDebouncedCurrentDisplayAmount, '0')
        ? getSimulateSendTxBytes({
            accountNumber: account.data.account.account_number,
            sequence: account.data.account.sequence,
            mnemonic: currentAccount.mnemonic,
            senderAddress: currentAccount.cosmosAddress,
            receiverAddress: currentReceiverAddress,
            amount: { amount: baseDebouncedCurrentDisplayAmount, denom: currentCoin.denom },
            memo: debouncedCurrentMemo,
          })
        : undefined,
    [
      account.data?.account.account_number,
      account.data?.account.sequence,
      baseDebouncedCurrentDisplayAmount,
      currentAccount.cosmosAddress,
      currentAccount.mnemonic,
      currentCoin,
      currentReceiverAddress,
      debouncedCurrentMemo,
    ],
  );

  const simulate = useSimulateSWR({ txBytes: simulateDirectTxBytes?.tx_bytes });

  const simulatedGas = useMemo(
    () => (simulate.data?.gas_info?.gas_used ? String(ceil(times(simulate.data.gas_info.gas_used, '1.2'))) : undefined),
    [simulate.data?.gas_info?.gas_used],
  );

  const currentGas = useMemo(() => simulatedGas || '200000', [simulatedGas]);

  const baseEstimateFeeAmount = useMemo(() => String(ceil(times(currentGas, '12500000000'))), [currentGas]);

  const displayEstimateFeeAmount = useMemo(() => toDisplayDenomAmount(baseEstimateFeeAmount, DYDX_TOKEN.decimals), [baseEstimateFeeAmount]);

  const sendDirectTx = useMemo(
    () =>
      account.data?.account.account_number &&
      currentAccount.mnemonic &&
      account.data?.account.sequence &&
      currentAccount.cosmosAddress &&
      baseEstimateFeeAmount &&
      !!currentCoin &&
      gt(baseDebouncedCurrentDisplayAmount, '0') &&
      !!currentGas
        ? getSendDirectDoc({
            accountNumber: account.data.account.account_number,
            sequence: account.data.account.sequence,
            mnemonic: currentAccount.mnemonic,
            senderAddress: currentAccount.cosmosAddress,
            receiverAddress: currentReceiverAddress,
            amount: { amount: baseDebouncedCurrentDisplayAmount, denom: currentCoin.denom },
            simulatedGas: currentGas,
            simulatedFeeAmount: baseEstimateFeeAmount,
            memo: debouncedCurrentMemo,
          })
        : undefined,
    [
      account.data?.account.account_number,
      account.data?.account.sequence,
      baseDebouncedCurrentDisplayAmount,
      baseEstimateFeeAmount,
      currentAccount.cosmosAddress,
      currentAccount.mnemonic,
      currentGas,
      currentReceiverAddress,
      debouncedCurrentMemo,
      currentCoin,
    ],
  );

  const txInfo = useTxInfoSWR(transactionHash);

  const transactionBodyText = useMemo(() => {
    if (isTxTrackingFailed) return 'Sending Failed';

    if (isTxTrackingSuccess) return 'Sending Complete';

    // return `Sending ${debouncedCurrentDisplayAmount} ${currentCoin?.symbol || ''} to ${currentReceiverAddress}`;
    return `Sending...`;
  }, [isTxTrackingFailed, isTxTrackingSuccess]);

  const transactionSubText = useMemo(() => (isTxTrackingSuccess ? '' : undefined), [isTxTrackingSuccess]);

  const maxDisplayAmount = useMemo(() => {
    const maxAmount = toDisplayDenomAmount(minus(currentCoinBaseAmount, times(baseEstimateFeeAmount, '1.1')), currentCoin?.decimals || 0);
    if (currentCoin?.denom === DYDX_TOKEN.denom) {
      return gt(maxAmount, '0') ? maxAmount : '0';
    }

    return toDisplayDenomAmount(currentCoinBaseAmount, currentCoin?.decimals || 0);
  }, [baseEstimateFeeAmount, currentCoin, currentCoinBaseAmount]);

  const errorMessage = useMemo(() => {
    if (!currentAccount.cosmosAddress) {
      return 'Please connect wallet';
    }

    if (!currentCoin) {
      return 'Please select coin';
    }

    if (!currentDisplayAmount || !gt(currentDisplayAmount, '0')) {
      return 'Please input amount';
    }

    if (gt(baseDebouncedCurrentDisplayAmount, currentCoinBaseAmount)) {
      return 'Insufficient balance';
    }

    if (currentCoin.denom === DYDX_TOKEN.denom && gt(baseDebouncedCurrentDisplayAmount, '0')) {
      if (gt(baseEstimateFeeAmount, minus(currentCoinBaseAmount, baseDebouncedCurrentDisplayAmount))) {
        return 'Insufficient DYDX balance for transaction fee';
      }
    }

    if (!currentReceiverAddress) {
      return 'Please input recipient address';
    }

    if (!addressRegex.test(currentReceiverAddress)) {
      return 'Please enter the dYdX address';
    }

    if (!sendDirectTx) {
      return 'Invalid Transaction';
    }

    return '';
  }, [
    addressRegex,
    baseDebouncedCurrentDisplayAmount,
    baseEstimateFeeAmount,
    currentAccount.cosmosAddress,
    currentCoin,
    currentCoinBaseAmount,
    currentDisplayAmount,
    currentReceiverAddress,
    sendDirectTx,
  ]);

  const txConfirmedStatus = useMemo(() => {
    if (txInfo.error?.response?.status && txInfo.error.response.status >= 400 && txInfo.error.response.status < 500) return 'pending';

    if (txInfo.data?.tx_response.code !== undefined) {
      if (txInfo.data.tx_response.code !== 0) return 'failed';

      if (txInfo.data.tx_response.code === 0) return 'confirmed';
    }

    return undefined;
  }, [txInfo.data?.tx_response.code, txInfo.error?.response?.status]);

  useEffect(() => {
    pageView('Transfer', 'Transfer');
  }, []);

  const debouncedEnabled = useDebouncedCallback(() => {
    setTimeout(() => {
      setIsDisabled(false);
    }, 700);
  }, 700);

  useEffect(() => {
    setIsDisabled(true);

    debouncedEnabled();
  }, [debouncedEnabled, sendDirectTx, currentMemo]);

  useEffect(() => {
    if (isOpenTrasactionBottomsheet) {
      if (txConfirmedStatus === 'confirmed') {
        setIsTxTrackingSucces(true);
      }
      if (txConfirmedStatus === 'failed' || txInfo.hasTimedOut) {
        setIsTxTrackingFailed(true);
      }
    }
  }, [isOpenTrasactionBottomsheet, txConfirmedStatus, txInfo.hasTimedOut]);

  useEffect(() => {
    if (!currentAccount.cosmosAddress) {
      setIsOpenSignConfirmBottomsheet(false);
      setIsOpenTrasactionBottomsheet(false);
    }
  }, [currentAccount.cosmosAddress]);

  return (
    <>
      <Container>
        <>
          <ContentsContainer>
            <Header>
              <HeaderTitle>
                <Typography variant="h1">TRANSFER</Typography>
              </HeaderTitle>
            </Header>
            <DYDXContainer>
              <DYDXTopContainer>
                <DYDXTopLeftContainer>
                  <Typography variant="h7">Available</Typography>
                  &nbsp;
                  <Typography variant="h4">:</Typography>
                  <DYDXTopLeftAmountContainer>
                    &nbsp;
                    <NumberTypo typoOfIntegers="h6" typoOfDecimals="h8">
                      {String(parseFloat(fix(toDisplayDenomAmount(currentCoinBaseAmount, currentCoin?.decimals || 0), 6)))}
                    </NumberTypo>
                  </DYDXTopLeftAmountContainer>
                </DYDXTopLeftContainer>
                <DYDXTopRightContainer>
                  <AmountPresetButton
                    onClick={() => {
                      setCurrentDisplayAmount(trimZeros(times(toDisplayDenomAmount(currentCoinBaseAmount, currentCoin?.decimals || 0), '0.25'), 18));
                    }}
                  >
                    <Typography variant="h10">1/4</Typography>
                  </AmountPresetButton>

                  <AmountPresetButton
                    onClick={() => {
                      setCurrentDisplayAmount(trimZeros(times(toDisplayDenomAmount(currentCoinBaseAmount, currentCoin?.decimals || 0), '0.5'), 18));
                    }}
                  >
                    <Typography variant="h10">HALF</Typography>
                  </AmountPresetButton>

                  <AmountPresetButton
                    onClick={() => {
                      setCurrentDisplayAmount(trimZeros(maxDisplayAmount, 18));
                    }}
                  >
                    <Typography variant="h10">MAX</Typography>
                  </AmountPresetButton>
                </DYDXTopRightContainer>
              </DYDXTopContainer>
              <DYDXBodyContainer>
                <TokenButton
                  onClick={() => {
                    setIsOpenedCoinBottomsheet(true);
                  }}
                >
                  {currentCoin?.denom === DYDX_TOKEN.denom ? (
                    <DYDXIconContainer>
                      <DYDXIcon />
                    </DYDXIconContainer>
                  ) : currentCoin?.denom === USDC_TOKEN.denom ? (
                    <DYDXIconContainer>
                      <USDCIcon />
                    </DYDXIconContainer>
                  ) : (
                    <ImageContainer>
                      <Image src={currentCoin?.image} />
                    </ImageContainer>
                  )}

                  <TokenTextContainer>
                    <TokenTextTopContainer>
                      <Typography variant="h4">{currentCoin?.symbol}</Typography>
                      <RightChevronIconContainer>
                        <RightChevronIcon />
                      </RightChevronIconContainer>
                    </TokenTextTopContainer>
                    <Base02TextContainer>
                      <Typography variant="h9">{currentCoin?.denom === DYDX_TOKEN.denom ? 'dydx Chain Token' : 'IBC Token'}</Typography>
                    </Base02TextContainer>
                  </TokenTextContainer>
                </TokenButton>
                <TokenRightContainer>
                  <StyledInput
                    onChange={(e) => {
                      if (!isDecimal(e.currentTarget.value, DYDX_TOKEN.decimals) && e.currentTarget.value) {
                        return;
                      }

                      setCurrentDisplayAmount(e.currentTarget.value);
                    }}
                    value={currentDisplayAmount}
                    placeholder="0"
                  />
                </TokenRightContainer>
              </DYDXBodyContainer>
            </DYDXContainer>

            <InputContainer>
              <Typography variant="h7">Recipient Address</Typography>

              <StyledTextarea
                placeholder="Enter the recipient's DYDX address"
                onChange={(e) => {
                  setCurrentReceiverAddress(e.currentTarget.value);
                }}
                value={currentReceiverAddress}
              />
            </InputContainer>

            <InputContainer>
              <Typography variant="h7">Memo (Optional)</Typography>

              <StyledTextarea
                multiline
                maxRows={2}
                placeholder="Do NOT input your personal data such as secret phrase."
                onChange={(e) => {
                  if (isMnemonic(e.currentTarget.value) || e.currentTarget.value.length > 256 || e.currentTarget.value.match(/^0x/)) {
                    return;
                  }

                  setCurrentMemo(e.currentTarget.value);
                }}
                value={currentMemo}
              />
            </InputContainer>

            <DetailContainer>
              <TxDetails estimatedGasFee={displayEstimateFeeAmount} />
            </DetailContainer>
          </ContentsContainer>
          <Tooltip varient="error" title={errorMessage} placement="top" arrow>
            <div>
              <GradientButton
                disabled={!!errorMessage || !sendDirectTx}
                onClick={() => {
                  setIsOpenSignConfirmBottomsheet(true);
                }}
              >
                <Typography variant="h4">TRANSFER</Typography>
              </GradientButton>
            </div>
          </Tooltip>
        </>
      </Container>

      <WrapperContainer data-is-open={isOpenedCoinBottomSheet}>
        <CoinBottomSheet
          open={isOpenedCoinBottomSheet}
          variant="persistent"
          coinList={availableCoins}
          onClickCoin={(coin) => {
            setCurrentCoinDenom(coin.denom);
            setCurrentDisplayAmount('');
            setIsOpenedCoinBottomsheet(false);
          }}
          onClose={() => {
            setIsOpenedCoinBottomsheet(false);
          }}
        />
      </WrapperContainer>

      <WrapperContainer data-is-open={isOpenSignConfirmBottomsheet}>
        <SignConfirmBottomSheet
          open={isOpenSignConfirmBottomsheet}
          variant="persistent"
          title="Transfer Details"
          descriptionText={`Review your token transfer and click "Confirm" to execute.`}
          amount={debouncedCurrentDisplayAmount}
          recipientAddress={currentReceiverAddress}
          currentCoin={currentCoin}
          displayEstimatedGasFeeAmount={displayEstimateFeeAmount}
          currentMemo={currentMemo}
          isButtonDisabled={isDisabled}
          isEstimation={!simulatedGas && !simulate.error}
          transitionDuration={0}
          onClickConfirm={async () => {
            try {
              if (!sendDirectTx || !account.data?.account.account_number) return;
              setIsOpenSignConfirmBottomsheet(false);
              setIsOpenTrasactionBottomsheet(true);

              const keypair = mnemonicToPair(currentAccount.mnemonic);
              const response = await signDirectTx(sendDirectTx, keypair, account.data.account.account_number);

              transferClickEvent('Transfer', 'Transfer');

              setTransactionHash(response.tx_response.txhash);

              enqueueSnackbar('Transaction broadcasting success', {
                variant: 'success',
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                txHash: response.tx_response.txhash,
                isCosmosTx: true,
              });

              await txInfo.mutate();
            } catch (e) {
              setIsTxTrackingFailed(true);

              enqueueSnackbar('Transaction Failed', {
                variant: 'error',
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                errorMsg: '',

                autoHideDuration: 3000,
              });

              setCurrentReceiverAddress('');
            }
          }}
          onClose={() => {
            setIsOpenSignConfirmBottomsheet(false);
            setCurrentMemo('');
          }}
        />
      </WrapperContainer>

      <WrapperContainer data-is-open={isOpenTrasactionBottomsheet}>
        <TransactionBottomSheet
          open={isOpenTrasactionBottomsheet}
          variant="persistent"
          title="Transfer Status"
          bodyText={transactionBodyText}
          subText={transactionSubText}
          isFailed={isTxTrackingFailed}
          isSuccess={isTxTrackingSuccess}
          isStaking
          txHash={transactionHash}
          transitionDuration={0}
          onClose={() => {
            setIsOpenTrasactionBottomsheet(false);
            setIsTxTrackingFailed(false);
            setIsTxTrackingSucces(false);
            setCurrentReceiverAddress('');
            setTransactionHash('');
            void balance.mutate();
            setCurrentDisplayAmount('');
            setCurrentMemo('');
          }}
        />
      </WrapperContainer>
    </>
  );
}
